import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { createRecipeStyles } from "./styles";
import { renderLicenseLink } from "./utils";
import { FetchedRecipe, Author, Contributor } from "../../types/recipe";
import { userNavigation } from "../../utils/navigation";

/**
 * Props for RecipeDetails component.
 * @property {FetchedRecipe} recipe - The recipe object to display details for.
 * @property {Author | null} author - The author of the recipe.
 * @property {Contributor[]} fullContributors - List of all contributors.
 */
interface RecipeDetailsProps {
  recipe: FetchedRecipe;
  author: Author | null;
  fullContributors: Contributor[];
}

/**
 * RecipeDetails Component
 *
 * Displays detailed information about a recipe, including author, contributors,
 * license, references, and difficulty. Uses responsive layout and Material UI
 * components for styling. All styles are centralized in styles.ts.
 *
 * Features:
 * - Shows author and contributors with profile links
 * - Displays license information with external link
 * - Lists references and additional links
 * - Renders difficulty as a row of dots
 * - Responsive and accessible layout
 *
 * @param {RecipeDetailsProps} props - Component props
 * @returns {JSX.Element} The rendered recipe details section
 */
const RecipeDetails: React.FC<RecipeDetailsProps> = ({
  recipe,
  author,
  fullContributors,
}) => {
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  // Shared responsive tokens (compact on md; roomier on lg/xl)
  const headingSize = {
    xs: "0.8rem",
    sm: "0.9rem",
    md: "0.9rem",
    lg: "1rem",
    xl: "1.05rem",
  };
  const bodySize = {
    xs: "0.75rem",
    sm: "0.82rem",
    md: "0.84rem",
    lg: "0.92rem",
    xl: "0.96rem",
  };
  const groupGap = { xs: 1, sm: 1.25, md: 1.5, lg: 1.75, xl: 2 };

  return (
    <Box
      // Main details container: vertical flex, gap between groups
      sx={{
        ...styles.page.detailsContainer,
        display: "flex",
        flexDirection: "column",
        gap: groupGap,
      }}
    >
      {/* Two sub-columns inside details (wraps on narrow widths) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          columnGap: { xs: 2, sm: 3, md: 4, lg: 5 },
          rowGap: { xs: 1.5, sm: 2, md: 2 },
        }}
      >
        {/* LEFT column: author, contributors, license */}
        <Box
          sx={{
            flex: "1 1 280px",
            display: "flex",
            flexDirection: "column",
            gap: groupGap,
          }}
        >
          {/* Author section */}
          {author && author._id && author.name ? (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  ...styles.page.detailTitle,
                  fontSize: headingSize,
                  lineHeight: 1.25,
                  mt: 0,
                }}
              >
                Created by
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ...styles.page.detailValue,
                  fontSize: bodySize,
                  lineHeight: 1.35,
                }}
              >
                {/* Author profile link */}
                <MuiLink
                  component={RouterLink}
                  to={userNavigation.getUserProfileUrl(author._id)}
                  sx={styles.page.link}
                >
                  {author.name}
                </MuiLink>
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: bodySize, lineHeight: 1.35 }}
            >
              No author listed
            </Typography>
          )}

          {/* Contributors section */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...styles.page.detailTitle,
                fontSize: headingSize,
                lineHeight: 1.25,
                mt: 0,
              }}
            >
              Authors / Contributors
            </Typography>
            <List disablePadding dense>
              {fullContributors.length > 0 ? (
                fullContributors.map((contributor) =>
                  contributor._id && contributor.name ? (
                    <ListItem key={contributor._id} disableGutters>
                      <ListItemText
                        primary={
                          // Contributor profile link
                          <MuiLink
                            component={RouterLink}
                            to={userNavigation.getUserProfileUrl(
                              contributor._id
                            )}
                            sx={styles.page.link}
                          >
                            {contributor.name}
                          </MuiLink>
                        }
                        primaryTypographyProps={{
                          sx: { fontSize: bodySize, lineHeight: 1.35 },
                        }}
                      />
                    </ListItem>
                  ) : null
                )
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: bodySize, lineHeight: 1.35 }}
                >
                  No contributors
                </Typography>
              )}
            </List>
          </Box>

          {/* License section */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...styles.page.detailTitle,
                fontSize: headingSize,
                lineHeight: 1.25,
                mt: 0,
              }}
            >
              License
            </Typography>
            {recipe.data.details.license ? (
              // License external link
              <MuiLink
                href={renderLicenseLink(recipe.data.details.license)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ...styles.page.link,
                  fontSize: bodySize,
                  lineHeight: 1.35,
                }}
              >
                {recipe.data.details.license}
              </MuiLink>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: bodySize, lineHeight: 1.35 }}
              >
                N/A
              </Typography>
            )}
          </Box>
        </Box>

        {/* RIGHT column: references, difficulty */}
        <Box
          sx={{
            flex: "1 1 280px",
            display: "flex",
            flexDirection: "column",
            gap: groupGap,
          }}
        >
          {/* References & Additional Links section */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...styles.page.detailTitle,
                fontSize: headingSize,
                lineHeight: 1.25,
                mt: 0,
              }}
            >
              References &amp; Additional Links
            </Typography>
            <List disablePadding dense>
              {Array.isArray(recipe.data.details.sources) &&
              recipe.data.details.sources.length > 0 ? (
                recipe.data.details.sources.map((source, index) =>
                  source.source && source.name ? (
                    <ListItem key={index} disableGutters>
                      <ListItemText
                        primary={
                          // Reference external link
                          <MuiLink
                            href={source.source}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              ...styles.page.link,
                              fontSize: bodySize,
                              lineHeight: 1.35,
                            }}
                          >
                            {source.name}
                          </MuiLink>
                        }
                      />
                    </ListItem>
                  ) : null
                )
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: bodySize, lineHeight: 1.35 }}
                >
                  No links
                </Typography>
              )}
            </List>
          </Box>

          {/* Difficulty section */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                ...styles.page.detailTitle,
                fontSize: headingSize,
                lineHeight: 1.25,
                mt: 0,
              }}
            >
              Difficulty
            </Typography>
            <Box sx={styles.page.difficultyContainer}>
              {/* Render difficulty dots (filled for difficulty, empty otherwise) */}
              {Array.from({ length: 5 }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    ...styles.page.difficultyDot,
                    width: { xs: 18, sm: 20, md: 22, lg: 26, xl: 28 },
                    height: { xs: 18, sm: 20, md: 22, lg: 26, xl: 28 },
                    bgcolor:
                      i <
                      (Number(recipe.data.details.difficulty) >= 0
                        ? Number(recipe.data.details.difficulty)
                        : 0)
                        ? theme.palette.materiom.seaweed
                        : "grey.400",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecipeDetails;
